import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { getAds, adminCreateAd, adminUpdateAd, adminDeleteAd } from "../lib/api";
import { Toolbar, Modal, Field, Check, Card, inputCls } from "./AdminLayout";
import { IconBtn } from "./ArticlesAdmin";
import { Pencil, Trash2 } from "lucide-react";
import { AdSlot, LeaderboardAd, NativeAd } from "../components/Ads";

const SLOTS = [
  { v: "leaderboard", l: "Leaderboard banner (728×90)" },
  { v: "sidebar", l: "Sidebar rectangle (300×250)" },
  { v: "native", l: "In-feed native / sponsored content" },
];
const empty = { slot: "leaderboard", label: "ADVERTISEMENT", format: "728x90 Leaderboard", sponsor: "", headline: "Your ad here", subtext: "", image: "", adsense: false };

export default function AdsAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => getAds().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (k) => () => setForm({ ...form, [k]: !form[k] });
  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...empty, ...a }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { slot: form.slot, label: form.label, format: form.format, sponsor: form.sponsor, headline: form.headline, subtext: form.subtext, image: form.image, adsense: form.adsense };
    setBusy(true);
    try {
      if (editing) { await adminUpdateAd(editing.id, payload); toast.success("Ad updated."); }
      else { await adminCreateAd(payload); toast.success("Ad created."); }
      setOpen(false); load();
    } catch { toast.error("Could not save ad."); }
    finally { setBusy(false); }
  };
  const remove = async (a) => { if (!window.confirm("Delete this ad unit?")) return; await adminDeleteAd(a.id); toast.success("Ad deleted."); load(); };

  const preview = (ad) => ad.slot === "leaderboard" ? <LeaderboardAd ad={ad} /> : ad.slot === "native" ? <NativeAd ad={ad} /> : <AdSlot ad={ad} className="rounded-lg" />;

  return (
    <div>
      <Toolbar title="Ads & Banners" subtitle="Locally-sold banners, native sponsored content, and Google AdSense remnant fill" onAdd={openNew} addLabel="New Ad Unit" />
      <div className="grid gap-5 lg:grid-cols-2">
        {items.map((a) => (
          <Card key={a.id} data-testid={`admin-ad-${a.id}`} className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-semibold capitalize text-slate-600">{a.slot}</span>
                {a.adsense && <span className="ml-2 rounded bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">AdSense</span>}
                {!a.adsense && a.sponsor && <span className="ml-2 rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">{a.sponsor}</span>}
              </div>
              <div className="flex gap-2">
                <IconBtn onClick={() => openEdit(a)} testid={`edit-ad-${a.id}`}><Pencil size={15} /></IconBtn>
                <IconBtn onClick={() => remove(a)} danger testid={`delete-ad-${a.id}`}><Trash2 size={15} /></IconBtn>
              </div>
            </div>
            {preview(a)}
          </Card>
        ))}
      </div>

      {open && (
        <Modal title={editing ? "Edit Ad Unit" : "New Ad Unit"} onClose={() => setOpen(false)} onSubmit={submit} busy={busy}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Placement"><select data-testid="ad-slot" value={form.slot} onChange={set("slot")} className={inputCls}>{SLOTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}</select></Field>
            <Field label="Label"><input value={form.label} onChange={set("label")} className={inputCls} placeholder="ADVERTISEMENT / SPONSORED" /></Field>
            <Field label="Format text"><input value={form.format} onChange={set("format")} className={inputCls} /></Field>
            <Field label="Sponsor (leave blank for AdSense)"><input data-testid="ad-sponsor" value={form.sponsor} onChange={set("sponsor")} className={inputCls} /></Field>
            <Field label="Headline" full><input data-testid="ad-headline" value={form.headline} onChange={set("headline")} className={inputCls} /></Field>
            <Field label="Subtext" full><textarea value={form.subtext} onChange={set("subtext")} rows={2} className={inputCls} /></Field>
            <Field label="Image URL (for native ads)" full><input value={form.image} onChange={set("image")} className={inputCls} /></Field>
            <div className="sm:col-span-2"><Check label="Serve via Google AdSense (remnant/network fill)" checked={form.adsense} onChange={toggle("adsense")} testid="ad-adsense" /></div>
          </div>
        </Modal>
      )}
    </div>
  );
}
